import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { fmtDateTime } from "../lib/format";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Gauge, ArrowsClockwise, Pause, Play, Database, Truck, Receipt,
  ShieldWarning, CloudArrowUp, ArrowSquareOut, Info, CheckCircle,
  Lock, ClockCounterClockwise, DownloadSimple, ArrowCounterClockwise, Copy,
} from "@phosphor-icons/react";

const fmtTime = (t) => (t ? fmtDateTime(t) : "Never");

/** Read-only LineUID readiness: shows how populated each stage is, and whether it's safe to
 *  flip Forward Dispatch Sync to live (gate = gsti invoice-line coverage). */
function CoverageBar({ label, cov, gate }) {
  if (!cov || !cov.available) {
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
          <span style={{ fontWeight: 600 }}>{label}{gate && <span style={{ color: "#0EA5E9" }}> · gate</span>}</span>
          <span className="muted">column not present yet</span>
        </div>
        <div style={{ height: 7, borderRadius: 4, background: "#E2E8F0" }} />
      </div>
    );
  }
  const pct = cov.pct || 0;
  const bg = pct >= 90 ? "#16A34A" : pct > 0 ? "#F59E0B" : "#CBD5E1";
  return (
    <div style={{ marginBottom: 8 }} data-testid={`readiness-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
        <span style={{ fontWeight: 600 }}>{label}{gate && <span style={{ color: "#0EA5E9" }}> · gate</span>}</span>
        <span className="text-mono" style={{ color: bg, fontWeight: 700 }}>{cov.with_uid}/{cov.total} · {pct}%</span>
      </div>
      <div style={{ height: 7, borderRadius: 4, background: "#E2E8F0", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: bg, transition: "width .4s ease" }} />
      </div>
    </div>
  );
}

function LineUidReadiness({ r }) {
  if (!r) return <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>Loading LineUID readiness…</div>;
  return (
    <div data-testid="lineuid-readiness" style={{ width: "100%", border: "1px solid var(--omh-divider, #E2E8F0)", borderRadius: 6, padding: "10px 12px", marginBottom: 10, background: "#F8FAFC" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#475569" }}>LineUID readiness (last {r.window_days}d)</span>
        <span data-testid="readiness-verdict" style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: r.ready ? "#DCFCE7" : "#FEF3C7", color: r.ready ? "#166534" : "#92400E" }}>
          {r.ready ? "READY TO FLIP LIVE" : "NOT READY — keep in shadow"}
        </span>
      </div>
      <CoverageBar label="gsti invoice lines (UID-eligible)" cov={r.gsti} gate />
      <CoverageBar label="po_dispatchi (DBA backfill)" cov={r.po_dispatchi} />
      <CoverageBar label="poi INTAKE (app-pushed)" cov={r.poi_intake} />
      {r.gsti_all && r.gsti_all.available && (
        <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>
          Raw all-gsti (incl. domestic/non-app invoices): <span className="text-mono">{r.gsti_all.with_uid}/{r.gsti_all.total} · {r.gsti_all.pct}%</span> — informational only; can never reach 90% because most rows are out of the app's scope.
        </div>
      )}
      <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
        Gate = <strong>app-scope gsti</strong> coverage (invoice lines whose PO the app manages) ≥90%. This excludes domestic/non-app invoices that will never carry a LineUID. The app-pushed <strong>poi</strong> lines are already stamped; the <code>po_dispatchi</code> bar tracks the DBA backfill. Multi-release items (e.g. monthly schedules) are intentionally left un-UID'd and handled by FIFO.
      </div>
    </div>
  );
}

/** One consistent job card: title, plain-English purpose, live status badge and actions. */
function JobCard({ icon: Icon, title, badge, badgeColor, purpose, meta, children, result, testid }) {
  return (
    <div className="jde-panel" style={{ marginTop: 12 }} data-testid={testid}>
      <div className="jde-panel-header">
        <span><Icon size={13} weight="bold" style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />{title}</span>
        {badge && (
          <span data-testid={`${testid}-badge`} style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
            background: badgeColor?.bg || "#E2E8F0", color: badgeColor?.fg || "#334155",
          }}>{badge}</span>
        )}
      </div>
      <div className="jde-panel-body">
        <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#475569", marginBottom: 10, alignItems: "flex-start" }}>
          <Info size={14} weight="bold" style={{ flexShrink: 0, marginTop: 1, color: "#0EA5E9" }} />
          <span>{purpose}</span>
        </div>
        {meta && <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>{meta}</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>
        {result && (
          <pre data-testid={`${testid}-result`} style={{
            marginTop: 10, background: "#0F172A", color: "#E2E8F0", fontSize: 11,
            padding: 10, borderRadius: 4, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap",
          }}>{typeof result === "string" ? result : JSON.stringify(result, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

export default function Cockpit() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [erpAuto, setErpAuto] = useState(null);

  const loadErpAuto = useCallback(() => {
    api.get("/replica/erp-invoice-sync/auto-status").then((r) => setErpAuto(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/admin/cockpit").then((r) => setData(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Failed to load cockpit");
    });
    loadErpAuto();
  }, [loadErpAuto]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/replica/lineuid-readiness").then((r) => setReadiness(r.data)).catch(() => {});
  }, []);

  // Generic action runner: sets busy, calls endpoint, stores result, toasts, reloads header stats.
  const run = async (key, method, url, { confirm, successMsg, body } = {}) => {
    if (confirm && !window.confirm(confirm)) return;
    setBusy(key);
    try {
      const r = method === "get" ? await api.get(url) : await api.post(url, body ?? {});
      setResults((s) => ({ ...s, [key]: r.data }));
      toast.success(successMsg || "Done");
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Operation failed";
      setResults((s) => ({ ...s, [key]: `ERROR: ${typeof msg === "string" ? msg : JSON.stringify(msg)}` }));
      toast.error(typeof msg === "string" ? msg : "Operation failed");
    } finally { setBusy(""); }
  };

  // ERP invoice sync = background job -> poll status until done. Separate from `run` (which is synchronous).
  const [erpBusy, setErpBusy] = useState("");
  const [erpRes, setErpRes] = useState(null);
  const [erpTracked, setErpTracked] = useState(true);
  const [erpSince, setErpSince] = useState("2025-01-01");
  const runErpSync = async (confirm) => {
    const key = confirm ? "erp-apply" : "erp-dry";
    if (confirm && !window.confirm("Apply ERP invoice sync? This upserts ERP sales into the app's invoices (idempotent, read-only on the ERP). Run a Dry Run first.")) return;
    setErpBusy(key); setErpRes(null);
    try {
      const { data } = await api.post("/replica/erp-invoice-sync", { confirm, since: erpSince, tracked_only: erpTracked });
      const jobId = data.job_id;
      // poll up to ~5 min
      for (let i = 0; i < 75; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = (await api.get(`/replica/erp-invoice-sync/status?job_id=${jobId}`)).data;
        if (s.status !== "running") {
          if (s.status === "error") { toast.error("Sync failed: " + (s.error || "")); setErpRes(s); }
          else { setErpRes(s.result); toast.success(confirm ? `Applied — ${s.result.upserted} upserted, ${s.result.backfilled || 0} blank invoices gap-filled` : `Dry run — ${s.result.candidates} to add, ${s.result.backfilled || 0} blank to gap-fill`); load(); }
          return;
        }
      }
      toast.error("Sync still running after 5 min — check again shortly");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start sync");
    } finally { setErpBusy(""); }
  };

  // MS SQL PO sync is now a BACKGROUND job (fixes the Cloudflare 524 on long prod syncs). Fire it,
  // then poll /sync/status until it's no longer running. Idempotent server-side — safe to re-run.
  // Collapse duplicate PUSHED intake POs (same customer + PO#) to one canonical doc, then re-sync
  // so the ERP open lines attach and the PO reappears in Open POs. Dry-run preview → confirm.
  const dedupePushedDupes = async () => {
    setBusy("dedupe");
    try {
      const { data: dry } = await api.post("/admin/po/dedupe-pushed-intake?confirm=false");
      setResults((s) => ({ ...s, dedupe: dry }));
      if (!dry.groups_with_dupes) {
        toast.success("No duplicate pushed POs found — nothing to clean up.");
        return;
      }
      const sample = (dry.groups || []).slice(0, 8).map((g) => `• PO ${g.customer_po} (${g.pcode}): ${g.count} copies → keep 1, retire ${g.retire_count}`).join("\n");
      if (!window.confirm(`Found ${dry.groups_with_dupes} customer PO(s) with duplicate pushed copies (${dry.would_retire} to retire, reversible backup kept):\n\n${sample}\n\nProceed to collapse each to one canonical copy, then run the ERP sync?`)) return;
      const { data: applied } = await api.post("/admin/po/dedupe-pushed-intake?confirm=true");
      setResults((s) => ({ ...s, dedupe: applied }));
      toast.success(`Retired ${applied.retired_total} duplicate(s). Running ERP sync to attach open lines…`);
      await runMssqlSync("dedupe-sync");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe failed");
    } finally { setBusy(""); }
  };


  const dedupeLineDupes = async () => {
    setBusy("linededupe");
    try {
      const { data: dry } = await api.post("/admin/po/dedupe-lines?confirm=false");
      setResults((s) => ({ ...s, linededupe: dry }));
      if (dry.ok === false) { toast.error(dry.error || "ERP not reachable"); return; }
      if (!dry.docs_changed) {
        toast.success("PO lines already match the ERP — nothing to reconcile.");
        return;
      }
      const sample = (dry.samples || []).slice(0, 8)
        .map((i) => `• PO ${i.custpo} (${i.customer_name || "?"}): ${i.lines_before} → ${i.lines_after} lines [dup ${i.removed_duplicate}, stale ${i.removed_stale}]`).join("\n");
      if (!window.confirm(`${dry.docs_changed} PO(s) need reconciling to the ERP — remove ${dry.removed_duplicate_lines} duplicate + ${dry.removed_stale_lines} stale revision-ghost line(s) (reversible backup kept). ${dry.protected_shipped_stale} shipped line(s) protected.\n\n${sample}\n\nProceed to reconcile these PO lines to the ERP?`)) return;
      const { data: applied } = await api.post("/admin/po/dedupe-lines?confirm=true");
      setResults((s) => ({ ...s, linededupe: applied }));
      toast.success(`Reconciled ${applied.docs_changed} PO(s): removed ${applied.removed_duplicate_lines} duplicate + ${applied.removed_stale_lines} stale line(s).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Line reconcile failed");
    } finally { setBusy(""); }
  };


  const materializeStuckPos = async () => {
    setBusy("materialize");
    try {
      const { data: dry } = await api.post("/admin/intake/materialize-open-orders?confirm=false");
      setResults((s) => ({ ...s, materialize: dry }));
      if (!dry.would_materialize) {
        toast.success(dry.already_current
          ? `Nothing to do — ${dry.already_current} PO(s) are already surfaced in Open Orders.`
          : "No stuck pushed POs found — all are already visible in Open Orders.");
        return;
      }
      const sample = (dry.items || []).filter((i) => !i.already_current).slice(0, 8)
        .map((i) => `• PO ${i.po_number} (${i.customer_name || "?"}): ${i.lines} line(s), ${i.qty_pending_total} open`).join("\n");
      if (!window.confirm(`${dry.would_materialize} pushed PO(s) are missing from Open Orders (no ERP lines yet). Rebuild their open lines from the intake data so they appear now?\n\n${sample}\n\n(Reversible — the ERP sync overwrites these with authoritative lines when it next runs.)`)) return;
      const { data: applied } = await api.post("/admin/intake/materialize-open-orders?confirm=true");
      setResults((s) => ({ ...s, materialize: applied }));
      toast.success(`Surfaced ${applied.materialized} PO(s) into Open Orders.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recover stuck POs failed");
    } finally { setBusy(""); }
  };


  const runMssqlSync = async (key = "mssql") => {
    setBusy(key);
    try {
      const { data } = await api.post("/sync/run");
      setResults((s) => ({ ...s, mssql: data }));
      toast.success(data.started === false ? "A sync is already running — watching for completion…"
                                           : "ERP sync started in background…");
      for (let i = 0; i < 75; i++) {           // poll up to ~5 min
        await new Promise((r) => setTimeout(r, 4000));
        const s = (await api.get("/sync/status")).data;
        if (!s.running) {
          setResults((st) => ({ ...st, mssql: s }));
          if (s.ok === false) toast.error("Sync failed — check logs");
          else toast.success(`Sync complete — ${s.po_updated ?? 0} updated, ${s.po_unchanged ?? 0} unchanged. Re-run the audit to confirm.`);
          load();
          return;
        }
      }
      toast.error("Sync still running after 5 min — check Sync Status shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start sync");
    } finally { setBusy(""); }
  };


  // Manually trigger one AUTOMATED-style pass (apply + reconcile, tracked-only) — same as the 6h poller.
  const runErpAutoNow = async () => {
    if (!window.confirm("Run the automated ERP invoice sync now? This applies + reconciles tracked-customer invoices (idempotent, read-only on the ERP).")) return;
    setErpBusy("erp-auto"); setErpRes(null);
    try {
      const { data } = await api.post("/replica/erp-invoice-sync/run-auto-now", {});
      const r = data.result || {};
      setErpRes(r);
      toast.success(`Auto-sync done — ${r.upserted} upserted, ${r.removed} removed`);
      loadErpAuto(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-sync run failed");
    } finally { setErpBusy(""); }
  };

  // ── Duplicate PO Cleanup ─────────────────────────────────────────────────────────────────
  const [dup, setDup] = useState(null);       // dry-run report {summary, groups}
  const [dupBusy, setDupBusy] = useState("");
  const [dupBatch, setDupBatch] = useState(null);
  const [dupShadow, setDupShadow] = useState(true);

  const dupDryRun = async () => {
    setDupBusy("dry"); setDupBatch(null);
    try {
      const { data } = await api.post("/admin/po-merge-duplicates", { apply: false, include_intake_shadow: dupShadow });
      setDup(data);
      toast.success(`Found ${data.summary.extra_docs} true duplicate(s) in ${data.summary.duplicate_groups} group(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    } finally { setDupBusy(""); }
  };

  const dupDownloadCsv = async () => {
    setDupBusy("csv");
    try {
      const { data } = await api.get("/admin/po-duplicates", { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `po_duplicates_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Duplicate report CSV downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setDupBusy(""); }
  };

  const dupApply = async () => {
    const s = dup?.summary;
    if (!s || !s.extra_docs) { toast.error("Run a dry-run first"); return; }
    if (!window.confirm(`Merge ${s.extra_docs} duplicate doc(s) — ${s.same_ms_pono_twins} identical-PoNo twin(s) + ${s.intake_shadows} intake shadow(s) — onto their survivors?\n\nEach removed doc is backed up first and is fully reversible. Distinct blanket-PO releases are NOT touched.`)) return;
    setDupBusy("apply");
    try {
      const { data } = await api.post("/admin/po-merge-duplicates", { apply: true, include_intake_shadow: dupShadow });
      setDupBatch(data.batch);
      setDup((d) => ({ ...(d || {}), summary: { ...(d?.summary || {}), extra_docs: 0, duplicate_groups: 0 }, groups: [] }));
      toast.success(`Merged ${data.retired} duplicate(s). Backup batch: ${data.batch} (reversible)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setDupBusy(""); }
  };

  const dupRevert = async () => {
    if (!dupBatch) return;
    if (!window.confirm(`Undo merge batch ${dupBatch}? This restores every doc it retired.`)) return;
    setDupBusy("revert");
    try {
      const { data } = await api.post(`/admin/po-merge-duplicates/revert?batch=${encodeURIComponent(dupBatch)}`);
      toast.success(`Reverted — restored ${data.restored} doc(s)`);
      setDupBatch(null); setDup(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally { setDupBusy(""); }
  };

  const m = data || {};
  const syncOn = m.mssql_sync?.enabled;
  const fwdApply = m.forward_dispatch_sync?.apply_mode;
  const audit = results.posync && typeof results.posync === "object" ? results.posync : null;

  return (
    <Layout breadcrumb={["Admin Cockpit"]}>
      <div className="jde-toolbar" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Gauge size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 12 }}>ADMIN COCKPIT — SYNC & BACKGROUND JOBS</span>
        </span>
        <button className="jde-btn" onClick={load} data-testid="cockpit-refresh">
          <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />Refresh
        </button>
      </div>

      {/* Orientation banner */}
      <div className="jde-panel" style={{ marginTop: 12, borderColor: "#BFDBFE" }}>
        <div className="jde-panel-body" style={{ background: "#EFF6FF", fontSize: 12, color: "#1E3A8A" }}>
          This cockpit consolidates every automated job and manual admin action in one place.
          Data flows: <strong>Excel → Mongo</strong> (open orders) → <strong>Mongo → live ERP</strong> (POs &amp; dispatch plans) →
          <strong> ERP invoices → Mongo</strong> (open-qty stays current, no re-import). Each card below explains exactly what it does.
          {m.server_time && <span> · Server time: <strong>{fmtTime(m.server_time)}</strong></span>}
        </div>
      </div>

      {/* At-a-glance stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 12 }} data-testid="cockpit-stats">
        <StatBox label="Open POs ready to push" value={m.live_po_push?.open_pos_available} icon={CloudArrowUp} />
        <StatBox label="Dispatches invoice-locked" value={m.dispatch_locks?.locked} sub={`of ${m.dispatch_locks?.tracked ?? "—"} tracked`} icon={Lock} />
        <StatBox label="Invoice poller high-water" value={m.invoice_status_poller?.highwater_sale_no} sub="last gst Sale_No seen" icon={Receipt} />
        <StatBox label="Last Excel import" value={fmtTime(m.excel_import?.last_run)} isText sub={m.excel_import?.source_file} icon={ClockCounterClockwise} />
      </div>

      {/* 0. PO Sync Health Audit — no-duplication + stuck-sync validation */}
      <JobCard
        testid="job-po-sync-audit"
        icon={CheckCircle}
        title="PO SYNC HEALTH  (no duplicates + stuck-sync check)"
        badge={audit ? (audit.healthy ? "✔ HEALTHY" : "⚠ NEEDS ATTENTION") : (typeof results.posync === "string" ? "ERROR" : "NOT RUN")}
        badgeColor={audit ? (audit.healthy ? { bg: "#DCFCE7", fg: "#166534" } : { bg: "#FEF3C7", fg: "#92400E" }) : { bg: "#E2E8F0", fg: "#334155" }}
        purpose={<>Validates the ERP→app PO sync: (1) <strong>no duplicate POs</strong> — the same ERP PoNo must never appear in more than one document; (2) <strong>stuck</strong> — pushed intake POs still missing their ERP <code>lines[]</code> (twin not synced yet, so they'd read as 0 qty in Monthly Sales). Read-only. Endpoint: <code>GET /api/admin/po-sync-audit</code></>}
        meta={audit ? <>Duplicates: <strong style={{ color: audit.duplicate_custpo_count ? "#DC2626" : "#16A34A" }}>{audit.duplicate_custpo_count}</strong> · Stuck (pushed-not-synced): <strong style={{ color: audit.stuck_count ? "#B45309" : "#16A34A" }}>{audit.stuck_count}</strong></> : null}
        result={results.posync}
      >
        <button className="jde-btn jde-btn-primary" disabled={busy === "posync"} onClick={() => run("posync", "get", "/admin/po-sync-audit", { successMsg: "Audit complete" })} data-testid="btn-po-sync-audit-run">
          <CheckCircle size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "posync" ? "Auditing…" : "Run PO Sync Audit"}
        </button>
        {audit && audit.stuck_count > 0 && (
          <button className="jde-btn" disabled={busy === "mssql"} onClick={() => runMssqlSync("mssql")} data-testid="btn-po-sync-audit-reconcile">
            <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "mssql" ? "Syncing…" : "Run ERP Sync (reconcile stuck)"}
          </button>
        )}
        <button className="jde-btn" disabled={busy === "dedupe" || busy === "dedupe-sync"} onClick={dedupePushedDupes} data-testid="btn-po-dedupe-pushed"
          title="Collapse duplicate PUSHED intake POs (same customer + PO#) to one canonical copy, then re-sync so the ERP open lines attach and the PO shows in Open POs again. Dry-run preview first; reversible.">
          <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "dedupe" ? "Scanning…" : busy === "dedupe-sync" ? "Syncing…" : "Fix duplicate pushed POs"}
        </button>
        <button className="jde-btn" disabled={busy === "materialize"} onClick={materializeStuckPos} data-testid="btn-po-materialize-stuck"
          title="Recover PUSHED POs that are missing from Open Orders because the ERP hasn't attached their open lines yet (e.g. ERP offline). Rebuilds each PO's open lines from its own intake data so it shows now. Dry-run preview first; reversible — the ERP sync overwrites with authoritative lines later.">
          <CheckCircle size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "materialize" ? "Recovering…" : "Recover stuck POs into Open Orders"}
        </button>
        <button className="jde-btn" disabled={busy === "linededupe"} onClick={dedupeLineDupes} data-testid="btn-po-dedupe-lines"
          title="Reconcile PO lines to the ERP: removes duplicate copies AND stale revision-ghost lines (e.g. an old due date the ERP no longer has) that show as phantom open lines or double-count on the Dispatch board. The ERP is the source of truth. Dry-run preview first; reversible.">
          <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "linededupe" ? "Reconciling…" : "Reconcile PO lines to ERP"}
        </button>
      </JobCard>

      {/* 0b. Duplicate PO Cleanup — blanket-PO-safe merge of TRUE duplicates */}
      <JobCard
        testid="job-po-dup-cleanup"
        icon={Copy}
        title="DUPLICATE PO CLEANUP  (true twins + intake shadows — blanket-safe)"
        badge={dup?.summary ? (dup.summary.extra_docs ? `${dup.summary.extra_docs} TO MERGE` : "✔ CLEAN") : "NOT RUN"}
        badgeColor={dup?.summary ? (dup.summary.extra_docs ? { bg: "#FEF3C7", fg: "#92400E" } : { bg: "#DCFCE7", fg: "#166534" }) : { bg: "#E2E8F0", fg: "#334155" }}
        purpose={<>Collapses only <strong>genuine</strong> duplicate PO docs onto their survivor: <strong>identical-PoNo twins</strong> (same ERP order stored twice) and <strong>un-enriched intake shadows</strong> (an app copy with no PoNo shadowed by its real ERP release). <strong>Distinct blanket-PO releases are never touched.</strong> Dry-run writes nothing; Apply backs up every removed doc first and is fully reversible. Endpoints: <code>/api/admin/po-duplicates</code> · <code>/api/admin/po-merge-duplicates</code></>}
        meta={dup?.summary ? <>Groups: <strong>{dup.summary.duplicate_groups}</strong> · To merge: <strong style={{ color: dup.summary.extra_docs ? "#B45309" : "#16A34A" }}>{dup.summary.extra_docs}</strong> (<strong>{dup.summary.same_ms_pono_twins}</strong> twins + <strong>{dup.summary.intake_shadows}</strong> shadows){dup.groups_truncated && <> · showing first 500 groups</>}</> : "Run a dry-run to preview the review sheet — writes nothing."}
        result={dupBatch ? { merged_batch: dupBatch, note: "Reversible — use Undo below or POST /admin/po-merge-duplicates/revert?batch=" + dupBatch } : null}
      >
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginRight: 4 }}>
          <input type="checkbox" checked={dupShadow} onChange={(e) => setDupShadow(e.target.checked)} data-testid="dup-shadow-toggle" />
          Include intake shadows
        </label>
        <button className="jde-btn jde-btn-primary" disabled={dupBusy === "dry"} onClick={dupDryRun} data-testid="btn-po-dup-dryrun">
          <CheckCircle size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{dupBusy === "dry" ? "Scanning…" : "Dry-run (preview sheet)"}
        </button>
        <button className="jde-btn" disabled={dupBusy === "csv"} onClick={dupDownloadCsv} data-testid="btn-po-dup-csv">
          <DownloadSimple size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{dupBusy === "csv" ? "Preparing…" : "Download review sheet (CSV)"}
        </button>
        <button className="jde-btn" disabled={dupBusy === "apply" || !dup?.summary?.extra_docs} onClick={dupApply} data-testid="btn-po-dup-apply"
          style={{ background: dup?.summary?.extra_docs ? "#B45309" : undefined, color: dup?.summary?.extra_docs ? "#fff" : undefined }}>
          <ShieldWarning size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{dupBusy === "apply" ? "Merging…" : "Apply merge (backup + reversible)"}
        </button>
        {dupBatch && (
          <button className="jde-btn" disabled={dupBusy === "revert"} onClick={dupRevert} data-testid="btn-po-dup-revert">
            <ArrowCounterClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{dupBusy === "revert" ? "Reverting…" : "Undo last merge"}
          </button>
        )}
        {dup?.groups?.length > 0 && (
          <div style={{ width: "100%", marginTop: 10, maxHeight: 320, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 6 }} data-testid="po-dup-table">
            <table className="jde-grid" style={{ width: "100%", fontSize: 11.5 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>PO key</th>
                  <th style={{ textAlign: "left" }}>Customer</th>
                  <th>Copies</th>
                  <th>Kept</th>
                  <th>Merge</th>
                  <th style={{ textAlign: "left" }}>Reasons</th>
                </tr>
              </thead>
              <tbody>
                {dup.groups.slice(0, 200).map((g) => {
                  const twins = g.remove.filter((x) => x.reason === "same_ms_pono_twin").length;
                  const shadows = g.remove.filter((x) => x.reason === "intake_shadow").length;
                  return (
                    <tr key={g.key}>
                      <td className="text-mono">{g.key}</td>
                      <td>{g.customer}</td>
                      <td style={{ textAlign: "center" }}>{g.copies}</td>
                      <td style={{ textAlign: "center", color: "#16A34A", fontWeight: 700 }}>{g.releases_kept}</td>
                      <td style={{ textAlign: "center", color: "#B45309", fontWeight: 700 }}>{g.remove.length}</td>
                      <td style={{ fontSize: 10.5 }}>
                        {twins > 0 && <span style={{ marginRight: 6 }}>{twins} twin</span>}
                        {shadows > 0 && <span style={{ color: "#7C3AED" }}>{shadows} shadow</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {dup.groups.length > 200 && <div className="muted" style={{ fontSize: 11, padding: 6 }}>Showing first 200 of {dup.groups.length} groups — download the CSV for the full sheet.</div>}
          </div>
        )}
      </JobCard>

      {/* 1. Legacy MS SQL sync */}
      <JobCard
        testid="job-mssql-sync"
        icon={Database}
        title="LEGACY MS SQL → MONGO SYNC"
        badge={data == null ? "…" : syncOn ? `● RUNNING (every ${m.mssql_sync?.interval_min}m)` : "❚❚ PAUSED"}
        badgeColor={syncOn ? { bg: "#DCFCE7", fg: "#166534" } : { bg: "#FEE2E2", fg: "#991B1B" }}
        purpose="The original one-way copy of POs & invoices from the legacy ERP into Mongo. Intentionally PAUSED — the app now learns dispatch/invoice progress precisely via LineUID (see Forward Dispatch Sync), so this bulk copy is a fallback only."
        meta={<>Last run: <strong>{fmtTime(m.mssql_sync?.last_run)}</strong>{m.mssql_sync?.po_inserted != null && <> · {m.mssql_sync.po_inserted} new / {m.mssql_sync.po_updated} updated / {m.mssql_sync.po_revised} revised POs</>}</>}
        result={results.mssql}
      >
        {syncOn ? (
          <button className="jde-btn" disabled={busy === "mssql-pause"} onClick={() => run("mssql", "post", "/sync/pause", { successMsg: "Auto-sync paused" })} data-testid="btn-mssql-pause">
            <Pause size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "mssql-pause" ? "Pausing…" : "Pause Auto-Sync"}
          </button>
        ) : (
          <button className="jde-btn jde-btn-primary" disabled={busy === "mssql-resume"} onClick={() => run("mssql", "post", "/sync/resume?run_now=true", { successMsg: "Auto-sync resumed" })} data-testid="btn-mssql-resume">
            <Play size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "mssql-resume" ? "Starting…" : "Resume Auto-Sync"}
          </button>
        )}
        <button className="jde-btn" disabled={busy === "mssql"} onClick={() => runMssqlSync("mssql")} data-testid="btn-mssql-run">
          <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "mssql" ? "Syncing…" : "Run Sync Now"}
        </button>
      </JobCard>

      {/* 2. Forward Dispatch Sync */}
      <JobCard
        testid="job-forward-dispatch"
        icon={Truck}
        title="FORWARD DISPATCH SYNC  (ERP gsti → Mongo open qty)"
        badge={fwdApply ? "● LIVE (writes)" : "◐ SHADOW (preview only)"}
        badgeColor={fwdApply ? { bg: "#DCFCE7", fg: "#166534" } : { bg: "#FEF3C7", fg: "#92400E" }}
        purpose="Reads invoiced quantities from the live ERP (gsti, grouped by LineUID) and decrements each Mongo release line's open qty — so the open-order view stays current WITHOUT re-importing an Excel dispatch tracker. Exact per-release match via LineUID; historical rows fall back to FIFO-by-delivery-date. Runs in SHADOW until post-cutover invoices carry LineUID."
        meta={<>Poll loop: <strong>{m.forward_dispatch_sync?.poll_min ? `every ${m.forward_dispatch_sync.poll_min}m` : "disabled (manual only)"}</strong> · Last run: <strong>{fmtTime(m.forward_dispatch_sync?.last_run)}</strong>{m.forward_dispatch_sync?.changed_lines != null && <> · {m.forward_dispatch_sync.changed_lines} lines changed</>}</>}
        result={results.fwd}
      >
        <LineUidReadiness r={readiness} />
        <button className="jde-btn" disabled={busy === "fwd-preview"} onClick={() => run("fwd", "post", "/dispatch-sync/run?apply=false", { successMsg: "Preview computed (no writes)" })} data-testid="btn-fwd-preview">
          {busy === "fwd-preview" ? "Computing…" : "Preview (dry run)"}
        </button>
        <button className="jde-btn" style={{ background: "#0EA5E9", color: "#fff", borderColor: "#0EA5E9" }} disabled={busy === "fwd-apply"}
          onClick={() => run("fwd", "post", "/dispatch-sync/run?apply=true", { confirm: "Apply Forward Dispatch Sync? This WRITES qty_shipped/qty_open to every Mongo release line.", successMsg: "Applied — Mongo open qty updated" })} data-testid="btn-fwd-apply">
          {busy === "fwd-apply" ? "Applying…" : "Apply Now (writes to Mongo)"}
        </button>
      </JobCard>

      {/* 3. Invoice-status poller (Layer B) */}
      <JobCard
        testid="job-invoice-poller"
        icon={Receipt}
        title="INVOICE-STATUS POLLER  (Layer B lock cache)"
        badge={m.invoice_status_poller?.poll_min ? `● RUNNING (every ${m.invoice_status_poller.poll_min}m)` : "❚❚ DISABLED"}
        badgeColor={m.invoice_status_poller?.poll_min ? { bg: "#DCFCE7", fg: "#166534" } : { bg: "#FEE2E2", fg: "#991B1B" }}
        purpose="A lightweight NOLOCK poller that reads new/changed ERP invoices (gst) and caches which dispatches have been invoiced. The dispatch list uses this cache to show lock badges instantly, so an already-invoiced dispatch plan can't be edited or deleted."
        meta={<>High-water Sale_No: <strong>{m.invoice_status_poller?.highwater_sale_no}</strong> · Last run: <strong>{fmtTime(m.invoice_status_poller?.last_run)}</strong></>}
        result={results.invpoll}
      >
        <button className="jde-btn" disabled={busy === "invpoll"} onClick={() => run("invpoll", "post", "/invoice-status/refresh", { successMsg: "Cache refreshed" })} data-testid="btn-invpoll-refresh">
          {busy === "invpoll" ? "Refreshing…" : "Refresh Now"}
        </button>
        <button className="jde-btn" disabled={busy === "invpoll-full"} onClick={() => run("invpoll", "post", "/invoice-status/refresh?full=true", { confirm: "Full rescan of ALL gst invoices from Sale_No 0? Slower but rebuilds the whole lock cache.", successMsg: "Full rescan complete" })} data-testid="btn-invpoll-full">
          {busy === "invpoll-full" ? "Rescanning…" : "Full Rescan"}
        </button>
      </JobCard>

      {/* 4. Unlinked-invoices watchdog (Layer C) */}
      <JobCard
        testid="job-watchdog"
        icon={ShieldWarning}
        title="UNLINKED-INVOICES WATCHDOG  (Layer C)"
        purpose="On-demand audit that flags post-cutover ERP invoices that carry a real invoice number but NO dispatch plan behind them. A zero result proves the DBA's 'no invoice without a dispatch plan' rule is holding. A non-zero result means invoices are being raised outside the app."
        result={results.watchdog}
      >
        <button className="jde-btn" disabled={busy === "watchdog"} onClick={() => run("watchdog", "get", "/invoice-status/unlinked", { successMsg: "Watchdog check complete" })} data-testid="btn-watchdog-run">
          {busy === "watchdog" ? "Checking…" : "Run Watchdog Check"}
        </button>
      </JobCard>

      {/* 5. Live PO push */}
      <JobCard
        testid="job-live-push"
        icon={CloudArrowUp}
        title="LIVE PO PUSH  (Mongo → live dbo.po / dbo.poi)"
        purpose="Upserts your OPEN Excel POs directly into the LIVE ERP order tables so dispatches can be created against them. Targeted INSERT/UPDATE only — never DELETE, so ERP history is never touched. Always Dry Run first to review the plan."
        meta={<>Open POs available to push: <strong>{m.live_po_push?.open_pos_available}</strong></>}
        result={results.live}
      >
        <button className="jde-btn" disabled={busy === "live-dry"} onClick={() => run("live", "post", "/replica/transfer-live", { body: { confirm: false }, successMsg: "Dry run complete (no writes)" })} data-testid="btn-live-dryrun">
          {busy === "live-dry" ? "Planning…" : "Dry Run"}
        </button>
        <button className="jde-btn" disabled={busy === "live-diff"} onClick={() => run("live", "get", "/replica/lineuid-diff", { successMsg: "Pre-flight LineUID diff complete" })} data-testid="btn-live-lineuid-diff">
          {busy === "live-diff" ? "Checking…" : "Pre-flight LineUID Diff"}
        </button>
        <button className="jde-btn" style={{ background: "#DC2626", color: "#fff", borderColor: "#DC2626" }} disabled={busy === "live-confirm"}
          onClick={() => run("live", "post", "/replica/transfer-live", { body: { confirm: true }, confirm: "Push OPEN POs into the LIVE ERP tables (dbo.po / dbo.poi)? This writes to production (targeted INSERT/UPDATE only — never DELETE). Run a Dry Run first to review the plan.", successMsg: "Live PO push complete" })} data-testid="btn-live-confirm">
          {busy === "live-confirm" ? "Pushing…" : "Push Live (writes to ERP)"}
        </button>
      </JobCard>

      {/* 6. Replica transfer (New_PO/New_POi) */}
      <JobCard
        testid="job-replica"
        icon={Database}
        title="REPLICA TRANSFER  (Mongo → dbo.New_PO / dbo.New_POi)"
        purpose="Full-replaces the MS SQL replica tables (New_PO/New_POi) from the current Mongo open orders — a safe staging copy the ERP team can validate before the live cutover. LineUID is copied across so releases stay matched. Never touches live po/poi."
        result={results.replica}
      >
        <button className="jde-btn" disabled={busy === "replica-dry"} onClick={() => run("replica", "post", "/replica/transfer", { body: { confirm: false }, successMsg: "Dry run complete" })} data-testid="btn-replica-dryrun">
          {busy === "replica-dry" ? "Planning…" : "Dry Run"}
        </button>
        <button className="jde-btn" style={{ background: "#0EA5E9", color: "#fff", borderColor: "#0EA5E9" }} disabled={busy === "replica-confirm"}
          onClick={() => run("replica", "post", "/replica/transfer", { body: { confirm: true }, confirm: "Full-replace dbo.New_PO / dbo.New_POi from current Mongo open orders?", successMsg: "Replica transfer complete" })} data-testid="btn-replica-confirm">
          {busy === "replica-confirm" ? "Transferring…" : "Transfer (full replace)"}
        </button>
        <button className="jde-btn" disabled={busy === "replica-verify"} onClick={() => run("replica", "get", "/replica/verify", { successMsg: "Verification complete" })} data-testid="btn-replica-verify">
          <CheckCircle size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />{busy === "replica-verify" ? "Verifying…" : "Verify"}
        </button>
      </JobCard>

      {/* 7. Dispatch (invoice) sync with ERP */}
      <JobCard
        testid="job-erp-invoice-sync"
        icon={Receipt}
        title="DISPATCH SYNC WITH ERP  (live dbo.Gst/Gsti → Sales / invoices)"
        purpose="Pulls real invoiced sales straight from the live ERP into the app so Monthly Sales / dashboards reflect actual dispatches that were never in the one-time Excel seed (e.g. AMPHENOL). READ-ONLY on the ERP. Idempotent (keyed on the real Sale_No) and it SKIPS any sale already covered by the Excel seed, so nothing double-counts. It also GAP-FILLS blank invoice numbers / shipped dates on matching Excel-seed invoices from the ERP (fill-only — never overwrites values Excel already provided) and flips those to ERP-managed. Always Dry Run first."
      >
        {/* Automated poller status */}
        <div data-testid="erp-auto-status" style={{ width: "100%", border: "1px solid #C7D2FE", borderRadius: 6, padding: "10px 12px", marginBottom: 10, background: "#EEF2FF" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#3730A3" }}>
              <ClockCounterClockwise size={14} weight="bold" />
              Automated sync
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: erpAuto?.enabled ? "#DCFCE7" : "#FEE2E2", color: erpAuto?.enabled ? "#166534" : "#991B1B" }}>
                {erpAuto?.enabled ? `EVERY ${Math.round((erpAuto.interval_minutes || 360) / 60)}H` : "DISABLED"}
              </span>
            </span>
            <button className="jde-btn" disabled={!!erpBusy} onClick={runErpAutoNow} data-testid="btn-erp-auto-now">
              {erpBusy === "erp-auto" ? "Running…" : "Run auto-sync now"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 8, fontSize: 11.5 }}>
            <span>Last run: <b className="text-mono">{fmtTime(erpAuto?.last_run_at)}</b>
              {erpAuto?.last_status && <span style={{ marginLeft: 6, fontWeight: 700, color: erpAuto.last_status === "ok" ? "#16A34A" : "#DC2626" }}>({erpAuto.last_status})</span>}
            </span>
            <span>Next run: <b className="text-mono">{fmtTime(erpAuto?.next_run_at)}</b></span>
            {erpAuto?.last_result && (
              <span className="muted">
                {erpAuto.last_result.upserted} upserted · {erpAuto.last_result.removed} removed · {erpAuto.last_result.skipped_covered_by_seed} seeded · scanned {erpAuto.last_result.erp_gst_scanned}
              </span>
            )}
          </div>
          {erpAuto?.last_error && <div style={{ color: "#DC2626", fontSize: 11, marginTop: 6 }}>Last error: {erpAuto.last_error}</div>}
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}>
          <input type="checkbox" checked={erpTracked} onChange={(e) => setErpTracked(e.target.checked)} data-testid="erp-tracked-only" />
          Tracked customers only <span className="muted">(excludes scrap / non-dashboard sales)</span>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}>
          Since
          <input type="date" value={erpSince} onChange={(e) => setErpSince(e.target.value)} data-testid="erp-since" style={{ fontSize: 12, padding: "2px 6px" }} />
        </label>
        <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 8 }}>
          <button className="jde-btn" disabled={!!erpBusy} onClick={() => runErpSync(false)} data-testid="btn-erp-dryrun">
            {erpBusy === "erp-dry" ? "Scanning ERP…" : "Dry Run"}
          </button>
          <button className="jde-btn" style={{ background: "#16A34A", color: "#fff", borderColor: "#16A34A" }} disabled={!!erpBusy} onClick={() => runErpSync(true)} data-testid="btn-erp-apply">
            {erpBusy === "erp-apply" ? "Syncing…" : "Apply Sync"}
          </button>
        </div>
        {erpRes && erpRes.customers && (
          <div style={{ width: "100%", marginTop: 12 }} data-testid="erp-sync-result">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              {erpRes.dry_run ? "Dry run" : "Applied"} · scanned {erpRes.erp_gst_scanned} ERP invoices ·{" "}
              <span style={{ color: "#16A34A" }}>{erpRes.dry_run ? erpRes.candidates + " to add" : erpRes.upserted + " upserted"}</span> ·{" "}
              {erpRes.backfilled > 0 && (
                <><span style={{ color: "#7C3AED" }} data-testid="erp-backfilled">{erpRes.backfilled} blank {erpRes.dry_run ? "to gap-fill" : "gap-filled"}</span> · </>
              )}
              <span className="muted">{erpRes.skipped_covered_by_seed} skipped (already seeded)</span> ·{" "}
              {erpRes.tracked_only ? "tracked only" : "ALL customers"}
            </div>
            <div style={{ maxHeight: 260, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 4 }}>
              <table className="jde-grid" style={{ width: "100%", fontSize: 11.5 }}>
                <thead><tr>
                  <th style={{ padding: "6px 8px" }}>Customer</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Invoices</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>New</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Amount (₹ base)</th>
                </tr></thead>
                <tbody>
                  {erpRes.customers.map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: "5px 8px" }}>{c.customer_name}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "Inter, sans-serif" }}>{c.count}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "Inter, sans-serif", color: c.new ? "#16A34A" : "#94A3B8" }}>{c.new}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "Inter, sans-serif" }}>{Math.round(c.amount).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                  {erpRes.customers.length === 0 && <tr><td colSpan={4} className="muted" style={{ padding: 12, textAlign: "center" }}>Nothing to sync — everything is already covered by the Excel seed.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {erpRes && erpRes.status === "error" && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>Error: {erpRes.error}</div>}
      </JobCard>

      {/* Related admin links */}
      <div className="jde-panel" style={{ marginTop: 12 }} data-testid="cockpit-links">
        <div className="jde-panel-header"><span>RELATED ADMIN AREAS</span></div>
        <div className="jde-panel-body" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="jde-btn" onClick={() => nav("/settings")} data-testid="link-settings">
            <ArrowSquareOut size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />Email Ingestion & Danger Zone (Settings)
          </button>
          <button className="jde-btn" onClick={() => nav("/import")} data-testid="link-import">
            <ArrowSquareOut size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />Excel Import
          </button>
          <button className="jde-btn" onClick={() => nav("/users")} data-testid="link-users">
            <ArrowSquareOut size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />User Admin
          </button>
        </div>
      </div>
    </Layout>
  );
}

function StatBox({ label, value, sub, icon: Icon, isText }) {
  return (
    <div className="jde-panel" style={{ margin: 0 }}>
      <div className="jde-panel-body" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Icon size={22} weight="bold" style={{ color: "#0EA5E9", flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: isText ? 13 : 22, fontWeight: 700, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value ?? "—"}
          </div>
          <div className="muted" style={{ fontSize: 11 }}>{label}{sub ? ` · ${sub}` : ""}</div>
        </div>
      </div>
    </div>
  );
}
